// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import BusinessProfileUserPinsGrid from 'app/common/react/components/profile/UserProfilePinGrid/BusinessProfileUserPinsGrid';
import DidItProfileFeed from 'app/common/react/components/do/aggregated-pins/did-it/DidItProfileFeed/DidItProfileFeed';
import i18n from 'app/common/lib/i18n';
import SectionHeader from 'app/common/react/components/profile/SectionHeader/SectionHeader';
import type { State as ReduxState } from 'app/common/redux/types';
import UserProfilePinGrid from 'app/common/react/components/profile/UserProfilePinGrid/UserProfilePinGrid';
import UserProfileScheduledPinOverview from 'app/common/react/components/ScheduledPin/UserProfileScheduledPinOverview';
import withResource from 'app/common/lib/react/withResource';
import withRouteData from 'app/common/lib/react/withRouteData';
import { compose } from 'redux';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import type { PinFeedFilterOptionsType, ProfileUser } from 'app/packages/pinterest-flow-types/user';
import withViewer, { type Viewer } from 'app/common/lib/react/withViewer';
import { Box, Container } from 'gestalt';

type Props = {
  params: {
    username: string,
  },
  pinFeedFilter?: PinFeedFilterOptionsType,
  scheduledPinCountFromRedux: ?number,
  user: {
    data: ProfileUser,
    isAtEnd: boolean,
    isFetching: boolean,
    isLoaded: boolean,
  },
  viewer: Viewer,
};

function UserProfilePinGridContainer({ params, ...props }: Props) {
  const {
    pinFeedFilter,
    scheduledPinCountFromRedux,
    user: { data: user },
    viewer,
  } = props;

  const isOwnProfile = viewer.isAuth && viewer.username === params.username;
  const scheduledPinCount = scheduledPinCountFromRedux || user.scheduled_pin_count || 0;
  const hasScheduledPin = isOwnProfile && viewer.isPartner && scheduledPinCount > 0;
  const shouldRenderCreatorPinsGrid = user.show_creator_profile && viewer.isAuth;
  const creatorFeed =
    pinFeedFilter === 'TRIES' ? (
      <DidItProfileFeed isOwnProfile={isOwnProfile} triedItUser={user} />
    ) : (
      <BusinessProfileUserPinsGrid
        isOwnProfile={isOwnProfile}
        user={user}
        username={params.username}
        pinFeedFilter={pinFeedFilter}
      />
    );
  return (
    user && (
      <Box>
        {hasScheduledPin && (
          <React.Fragment>
            <UserProfileScheduledPinOverview username={params.username} />
            <Container>
              <SectionHeader
                label={i18n._('Published Pins', 'title for published Pins')}
                sublabel={interpolateNamedTemplate(
                  i18n.ngettext(
                    '{{ pinCount }} Pin',
                    '{{ pinCount }} Pins',
                    user.pin_count,
                    'Label for the number of pins a user has',
                  ),
                  {
                    pinCount: user.pin_count,
                  },
                )}
              />
            </Container>
          </React.Fragment>
        )}
        {shouldRenderCreatorPinsGrid ? (
          creatorFeed
        ) : (
          <UserProfilePinGrid
            id={user.id}
            isOwnProfile={isOwnProfile}
            pinCount={user.pin_count}
            showNativePins
            user={user}
            username={params.username}
          />
        )}
      </Box>
    )
  );
}

const mapStateToProps = (state: ReduxState, props: Props) => {
  const user = props.viewer.isAuth ? state.users[props.viewer.id] : undefined;
  const pinFeedFilter = state.ui.views && state.ui.views.updatedPinFeedFilter;

  return {
    scheduledPinCountFromRedux: user ? user.scheduled_pin_count : null,
    pinFeedFilter,
  };
};

export default compose(
  withViewer,
  withResource({
    name: 'UserResource',
    key: 'user',
    options: ({ params }) => ({
      field_set_key: 'profile',
      username: params.username,
    }),
  }),
  withRouteData({
    name: 'PinsList',
    routeContextLog: (context: Object, params: { username: string }) => {
      let userId;
      if ((context.user || {}).username === params.username) {
        userId = (context.user || {}).id;
      }
      return {
        object_id_str: userId,
        view_type: PCONST.thrift.ViewType.USER,
        view_parameter: PCONST.thrift.ViewParameterType.USER_PINS,
      };
    },
  }),
  connect(mapStateToProps),
)(UserProfilePinGridContainer);
